import React from 'react';
import { graphql, Link } from 'gatsby';
import marked from 'marked';

import BaseLayout from '../layouts/base-layout';
import BigLink from '../components/common/big-link';
import CDNImage from '../components/common/cdn-image';
import CollectionsGrid from '../components/collections/collections-grid';
import ContributorByline from '../components/contributors/contributor-byline';
import EssayGridCell from '../components/essays/essay-grid-cell';
import FriendsPromo from '../components/common/promos/friends-promo'
import GridHeader from '../components/common/grid-header';
import HeadWrapper from '../components/layout/head-wrapper';
import HomepageEssayGrid from '../components/essays/homepage-essay-grid';
import NewsletterSignup from '../components/common/promos/newsletter-signup';
import ShoppingBar from '../components/common/promos/shopping-bar';
import SmallLinkList from '../components/homepage/small-link-list';
import TopicLinks from '../components/common/topic-links';

import { randomElement, shuffle } from '../helpers/data-helpers';

import '../styles/homepage.scss';

export const Head = ({ data }) => (
	<HeadWrapper />
);

class Homepage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      editorsPicks: null,
      popularPosts: null,
      printImageNode: null,
    };
  }

  componentDidMount() {
    this.setState({
      editorsPicks: shuffle(this.props.data.picks.edges).slice(0, 5),
      printImageNode: randomElement(this.props.data.printImages.edges).node,
      popularPosts: shuffle(this.props.data.popular.edges).slice(0, 5),
    });
  }

  render() {
    const featuredConjecture = this.props.data.featuredConjecture.edges[0].node.data;
    

    return (
      <BaseLayout path={this.props.location.pathname} homepage={true}>
        <div className="page-width">
          <div className="homepage__top-grid">
            <div className="homepage__top-grid__feature-image">
              {this.props.data.recentEssays.edges.slice(0,1).map(({ node }, idx) => (
                <React.Fragment key={`featured-image-${idx}`}>
                  {node.data.Featured_Image_Path ? (
                    <div className="featured-image-wrapper">
                      <Link to={`/essay/${node.data.Slug}`}>
                        <CDNImage
                          imagePath={node.data.Featured_Image_Path}
                          alt={node.data.Title}
                        />
                      </Link>
                    </div>
                  ) : null}
                </React.Fragment>
              ))}
            </div>
            <div className="homepage__top-grid__feature-details">
              {this.props.data.recentEssays.edges.slice(0,1).map(({ node }, idx) => {
                const date = new Date(node.data.Published_Date);
                const utcDate = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
                
                const day = new Intl.DateTimeFormat('en-US', { day: 'numeric' }).format(utcDate);
                const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(utcDate);
                const year = new Intl.DateTimeFormat('en-US', { year: 'numeric' }).format(utcDate);
                
                const dateline = `${day} ${month} ${year}`;
                
                return (
                  <div className="details" key={`feature-details-${idx}`}>
                    <p className="title">
                      <Link
                        to={`/essay/${node.data.Slug}`}
                        dangerouslySetInnerHTML={{ __html: marked(node.data.Title).replace('<p>', '').replace('</p>', '')}}
                      />
                    </p>
                    {(node.data.Contributors) ? (
                      <p className="byline">
                        <ContributorByline contributors={node.data.Contributors} />
                      </p>
                    ) : null}
                    <p className="summary">
                      {node.data.Intro ? (
                        <span dangerouslySetInnerHTML={{ __html: marked(node.data.Intro).replace('<p>','').replace('</p>','')}} />
                      ) : null}
  
                      <Link className="read-more" to={`/essay/${node.data.Slug}`}>more</Link>
                    </p>
                    <div className="meta-bar">
                      <div>
                        <TopicLinks categories={node.data.Categories} />
                      </div>
                      <div
                        className="dateline"
                        dangerouslySetInnerHTML={{ __html: dateline }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="homepage__top-grid__intro">
              <p>
                <b><i>The Public Domain Review</i></b> is dedicated to the exploration of curious and compelling works from the history of art, literature, and ideas – focusing on works now fallen into the public domain, the vast commons of out-of-copyright material that everyone is free to enjoy, share, and build upon without restrictions.
              </p>
            </div>
            <div className="homepage__top-grid__quotes">
              <div>
                <p className="quotation">
                  “Magnificent … A model of digital curation”
                </p>
                <p className="attribution">
                  The Guardian
                </p>
              </div>
              <div>
                <p className="quotation">
                  “… a gold mine of fantastic images and stories.”
                </p>
                <p className="attribution">
                  The New York Times
                </p>
              </div>
            </div>
            <div className="homepage__top-grid__prints">
              <div className="homepage__prints-promo">
                <Link to="/shop/fine-art-prints">
                  <span className="headline">
                    Visit Our Online Shop
                  </span>

                  {this.state.printImageNode ? (
                    <CDNImage imagePath={this.state.printImageNode.data.Image_File_Path} alt="Sample Print" width={360} />
                  ) : null}
                  <span className="message">
                    Explore our selection of fine art prints, all custom made to the highest standards, framed or unframed, and shipped to your door.
                  </span>
                  <span className="action">
                    Start Exploring
                  </span>
                </Link>
              </div>
            </div>
            <div className="homepage__top-grid__recent-essays">
              <GridHeader title="Latest Essays" />
              <HomepageEssayGrid
                essayEdges={this.props.data.recentEssays.edges.slice(1)}
              />
              <BigLink label="Browse More Essays…" to="/essays" />
            </div>
            <div className="homepage__top-grid__newsletter">
              <NewsletterSignup formLocation="homepage" />
            </div>
            <div className="homepage__top-grid__friends">
              <FriendsPromo />
            </div>
          </div>
        </div>

        <div className="page-width">
          <div>
            <GridHeader title="Latest Collections" />
            <CollectionsGrid
              collections={this.props.data.recentCollections.edges}
            />
            <BigLink to="/collections" label="Browse More Collections…" />
          </div>
        </div>

        <div className="page-width">
          <div className="homepage__picks-row">
            <div>
              <GridHeader title="Editor’s Picks" />
              {this.state.editorsPicks ? (
                <SmallLinkList postEdges={this.state.editorsPicks} />
              ) : null}
            </div>
            <div>
                <GridHeader title="Conjectures" />
                <EssayGridCell
                  slug={featuredConjecture.Slug}
                  title={featuredConjecture.Title}
                  featuredImagePath={featuredConjecture.Featured_Image_Path}
                  summary={featuredConjecture.Intro}
                  contributors={featuredConjecture.Contributors}
                  publishedDate={featuredConjecture.Published_Date}
                />

                <BigLink label="Browse Conjectures…" to="/series/conjectures" />
            </div>
            <div>
              <GridHeader title="Popular Posts" />
              {this.state.popularPosts ? (
                <SmallLinkList postEdges={this.state.popularPosts} />
              ) : null}
            </div>
          </div>
        </div>

        <div className="page-width">
          <ShoppingBar />
        </div>
      </BaseLayout>
    );
  }
}

export default Homepage;

export const query = graphql`
{
  recentCollections:allAirtable(
    filter:{table:{eq:"Collections"}, data: {Published: {eq: true}}},
    limit: 8,
    sort: {data: {Published_Date: DESC}}
  ) {
    edges {
      node {
        data {
          Title
          Intro
          Slug
          Medium
          Theme
          Featured_Image_Path
        }
      }
    }
  }
  printImages:allAirtable(filter:{table:{eq:"Prints"}, data:{Published: {eq: true}}}) {
    edges {
      node {
        data {
          Image_File_Path
        }
      }
    }
  }
  featuredConjecture:allAirtable(
    filter:{table:{eq:"Essays"}, data: {Published: {eq: true}, Series: {eq: "Conjectures"}}}, limit: 1,
    sort:{data: {Published_Date: DESC}}
  ) {
    edges {
      node {
        data {
          Title
          Intro
          Slug
          Published_Date
          Featured_Image_Path
          Contributors {
            data {
              Name
              Slug
            }
          }
        }
      }
    }
  }
  recentEssays:allAirtable(filter:{table:{eq:"Essays"}, data: {Published: {eq: true}, Series: {eq: null}}},
              limit: 5,
              sort:{data: {Published_Date: DESC}}) {
    edges {
      node {
        data {
          Title
          Intro
          Slug
          Published_Date
          Categories
          Featured_Image_Path
          Contributors {
            data {
              Name
              Slug
            }
          }
        }
      }
    }
  }
  categories:allAirtable(filter:{table:{eq:"Essays"}, data:{Published: {eq: true}, Series: {eq: null}}}) {
    edges {
      node {
        data {
          Categories
        }
      }
    }
  }
  picks: allAirtable(filter: {table: {in: ["Essays", "Collections"]},
                              data: {Published: {eq: true}, Editor_Pick:{eq:true}}}) {
    edges {
      node {
        table
        data {
          Title
          Slug
          Featured_Image_Path
        }
      }
    }
  }
  popular: allAirtable(filter:{table: {in: ["Essays", "Collections"]},
                               data: {Published: {eq:true}, Popular_Post: {eq:true}}}) {
    edges {
      node {
        table
        data {
          Title
          Slug
          Featured_Image_Path
        }
      }
    }
  }
}
`;
